/**
 * Onboarding Checklist Card
 *
 * Floating card that shows new user onboarding progress:
 * - Displays a checklist of onboarding tasks
 * - Shows completion progress with visual indicator
 * - Links to guided workflows for each task
 * - Can be dismissed permanently
 * - Floats in bottom-left corner (opposite to help panel)
 *
 * Uses database-driven tasks from onboarding_checklist table.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  CheckCircle2,
  Circle,
  X,
  ChevronDown,
  ChevronUp,
  Sparkles,
  ArrowRight,
  Rocket,
  // Dynamic icon imports for task icons
  UserCircle,
  Car,
  User,
  Compass,
  Building,
  Settings,
  FileText,
  Shield,
  Bell,
  Calendar,
} from 'lucide-react';
import { helpAPI } from '../../../api';
import { useGuidedExperience } from './GuidedExperienceProvider';
import { useVisualHelp } from './VisualHelpProvider';
import { useTour } from '../tour';

// Icon mapping for dynamic icons from database
const ICON_MAP = {
  UserCircle,
  Car,
  User,
  Compass,
  Building,
  Settings,
  FileText,
  Shield,
  Bell,
  Calendar,
  Circle, // fallback
};

/**
 * Get icon component by name
 */
function getIcon(iconName) {
  return ICON_MAP[iconName] || Circle;
}

export function OnboardingChecklistCard() {
  const [isLoading, setIsLoading] = useState(true);
  const [data, setData] = useState(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isVisible, setIsVisible] = useState(true);
  const [animatingTask, setAnimatingTask] = useState(null);

  const { startWorkflow: startGuidedWorkflow, workflows, activeWorkflow } = useGuidedExperience();
  const { navigateTo } = useVisualHelp();
  const { startTour, isActive: isTourActive } = useTour();

  // Fetch onboarding data
  const fetchData = useCallback(async () => {
    const result = await helpAPI.getOnboarding();
    if (result.ok && result.data) {
      setData(result.data);
      // Hide card if dismissed or 100% complete
      if (result.data.dismissed || result.data.percentage === 100) {
        setIsVisible(false);
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Mark task complete via API (no event handling, reusable)
  const markTaskComplete = async (taskCode) => {
    setAnimatingTask(taskCode);
    setData(prev => {
      if (!prev) return prev;
      const newCompleted = prev.completed + 1;
      const newTotal = prev.total;
      return {
        ...prev,
        tasks: prev.tasks.map(t =>
          t.task_code === taskCode
            ? { ...t, completed: true, completed_at: new Date().toISOString() }
            : t
        ),
        completed: newCompleted,
        percentage: newTotal > 0 ? Math.round((newCompleted / newTotal) * 100) : 0,
      };
    });
    try {
      await helpAPI.completeOnboardingTask(taskCode);
    } catch {
      // Keep optimistic update
    }
    setTimeout(() => {
      setAnimatingTask(null);
      fetchData();
    }, 500);
  };

  // Handle task click - mark complete AND navigate/start workflow
  const handleTaskClick = async (task) => {
    if (task.completed) return;

    // Always mark the task as complete when clicked
    markTaskComplete(task.task_code);

    // If task has a target_action, handle it
    if (task.target_action) {
      if (task.target_action === 'start_tour') {
        startTour('main');
        return;
      }
    }

    // If task has a linked workflow, start it
    if (task.workflow_code) {
      const workflow = workflows.find(w => w.workflow_code === task.workflow_code);
      if (workflow) {
        startGuidedWorkflow(task.workflow_code);
        return;
      }
    }

    // Otherwise navigate to target view
    if (task.target_view) {
      navigateTo(task.target_view);
    }
  };

  // Handle checkbox click (stop propagation to avoid double-complete from row click)
  const handleCompleteTask = (taskCode, e) => {
    e.stopPropagation();
    markTaskComplete(taskCode);
  };

  // Handle dismiss - always hide locally, even if API call fails
  const handleDismiss = async () => {
    setIsVisible(false);
    try {
      await helpAPI.dismissOnboarding();
    } catch {
      // Dismiss locally even if server fails
    }
  };

  // Don't render if not visible or loading with no data
  if (!isVisible || (isLoading && !data)) {
    return null;
  }

  // Don't render if no tasks
  if (!data?.tasks?.length) {
    return null;
  }

  // Hide when tour or guided workflow is active
  if (isTourActive || activeWorkflow) {
    return null;
  }

  const { tasks, completed, total, percentage } = data;
  const isAllComplete = percentage === 100;

  return (
    <div
      className="fixed bottom-4 left-4 z-[70] w-80 flex flex-col bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden animate-fade-in"
      style={{ pointerEvents: 'auto' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-amber-500 to-orange-500 text-white">
        <div className="flex items-center gap-2">
          <Rocket className="w-5 h-5" />
          <span className="font-semibold">Demarrage rapide</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-1.5 hover:bg-white/20 rounded transition-colors"
            title={isExpanded ? 'Reduire' : 'Agrandir'}
          >
            {isExpanded ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
          <button
            onClick={handleDismiss}
            className="p-1.5 hover:bg-white/20 rounded transition-colors"
            title="Masquer"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Progress bar */}
      <div className="px-4 py-2 bg-amber-50 border-b border-amber-100">
        <div className="flex items-center justify-between text-sm mb-1">
          <span className="text-amber-800 font-medium">
            {completed}/{total} etapes terminees
          </span>
          <span className="text-amber-600 font-bold">{percentage}%</span>
        </div>
        <div className="w-full h-2 bg-amber-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all duration-500"
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {/* Expandable content */}
      {isExpanded && (
        <div className="flex-1 max-h-64 overflow-y-auto">
          {/* Task list */}
          <div className="p-3 space-y-1">
            {tasks.map((task) => {
              const IconComponent = getIcon(task.icon);
              const isAnimating = animatingTask === task.task_code;

              return (
                <div
                  key={task.task_code}
                  onClick={() => handleTaskClick(task)}
                  className={`
                    flex items-center gap-3 p-3 rounded-lg transition-all cursor-pointer group
                    ${task.completed
                      ? 'bg-green-50 border border-green-100'
                      : 'bg-gray-50 hover:bg-amber-50 border border-gray-100 hover:border-amber-200'
                    }
                    ${isAnimating ? 'scale-95 opacity-50' : ''}
                  `}
                >
                  {/* Checkbox/Status */}
                  <div className="flex-shrink-0">
                    {task.completed ? (
                      <CheckCircle2 className="w-5 h-5 text-green-500" />
                    ) : (
                      <button
                        onClick={(e) => handleCompleteTask(task.task_code, e)}
                        className="w-5 h-5 rounded-full border-2 border-gray-300 hover:border-amber-500 hover:bg-amber-50 transition-colors flex items-center justify-center"
                        title="Marquer comme termine"
                      >
                        <Circle className="w-3 h-3 text-transparent group-hover:text-amber-400" />
                      </button>
                    )}
                  </div>

                  {/* Icon */}
                  <div
                    className={`flex-shrink-0 p-1.5 rounded ${
                      task.completed ? 'bg-green-100' : 'bg-gray-100 group-hover:bg-amber-100'
                    }`}
                  >
                    <IconComponent
                      className={`w-4 h-4 ${
                        task.completed ? 'text-green-600' : 'text-gray-500 group-hover:text-amber-600'
                      }`}
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div
                      className={`text-sm font-medium ${
                        task.completed ? 'text-green-700 line-through' : 'text-gray-800'
                      }`}
                    >
                      {task.title}
                    </div>
                    {task.description && !task.completed && (
                      <div className="text-xs text-gray-500 truncate">{task.description}</div>
                    )}
                  </div>

                  {/* Arrow for incomplete tasks */}
                  {!task.completed && (
                    <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-amber-500 flex-shrink-0" />
                  )}
                </div>
              );
            })}
          </div>

          {/* Completion celebration */}
          {isAllComplete && (
            <div className="px-4 pb-4 text-center">
              <div className="flex items-center justify-center gap-2 text-green-600 font-medium">
                <Sparkles className="w-5 h-5" />
                Felicitations ! Vous etes pret !
                <Sparkles className="w-5 h-5" />
              </div>
              <button
                onClick={handleDismiss}
                className="mt-2 text-sm text-gray-500 hover:text-gray-700 underline"
              >
                Fermer ce panneau
              </button>
            </div>
          )}
        </div>
      )}

      {/* Minimized hint */}
      {!isExpanded && (
        <div className="px-4 py-2 text-sm text-gray-500 bg-gray-50">
          {percentage}% complete - Cliquez pour voir les etapes
        </div>
      )}
    </div>
  );
}

export default OnboardingChecklistCard;
